"use client"

import { useEffect, useState } from "react"
import { FeedData as Feed } from "@extractus/feed-extractor"
import { FileInput } from "./FileInput"
import { FeedDescription } from "./FeedDescription"
import { PostDescription } from "./PostDescription"
import { ImportOptions as ImportOptionsComponent } from "./ImportOptions"
import { ImportProgress } from "./ImportProgress"
import { ImportCompleteModal } from "../ImportCompleteModal/index"
import { BackButton } from "./BackButton"
import { ErrorAlert } from "./ErrorAlert"
import { ImportActionButton } from "./ImportActionButton"
import { ImportOptions } from "../../types"
import useRSSFile from "./hooks/useRSSFile"
import useRSSImport from "./hooks/useRSSImport"

/**
 * Interface defining props for the RSSImport component
 */
interface RSSImportProps {
  feed: Feed
  url: string
  onBack: () => void
}

/**
 * Component that handles RSS and file-based post imports
 * Manages file upload, processing, and batch import
 * Uses separate hooks for different concerns
 */
export function RSSImport({ feed, url, onBack }: RSSImportProps) {
  // Component-specific state
  const [exportFile, setExportFile] = useState<File | null>(null)

  // File processing related state and logic
  const { isFileLoading, posts, fileError, handleCreateNotes } = useRSSFile(
    feed,
    url,
    exportFile
  )

  // Import options state and logic
  const [importOptions, setImportOptions] = useState<ImportOptions>({
    overwrite: false,
    storeOnArweave: false,
  })

  // Import process state and logic
  const {
    importStatus,
    isImportLoading,
    importError,
    numFinished,
    skippedPosts,
    erroredPosts,
    importJobDetails,
    postsRemaining,
    handleImportPosts,
  } = useRSSImport(posts, importOptions)

  // Modal state
  const [showCompleteModal, setShowCompleteModal] = useState(false)

  useEffect(() => {
    // Update modal visibility when import is finished
    if (importStatus === "FINISHED" && importJobDetails && !showCompleteModal) {
      setShowCompleteModal(true)
    }
  }, [importStatus])

  // Combined loading state
  const isLoading = isFileLoading || isImportLoading

  // Combined error state
  const error = fileError || importError

  return (
    <div className="space-y-4">
      {/* Source description and inputs (only shown before import starts) */}
      {importStatus !== "IN_PROGRESS" && (
        <>
          <div>
            {posts ? (
              <PostDescription posts={posts} feed={feed} />
            ) : (
              <FeedDescription feed={feed} />
            )}
          </div>

          {/* Only show file input if we haven't processed a file yet */}
          {!posts && (
            <FileInput
              exportFile={exportFile}
              setExportFile={setExportFile}
              disabled={isLoading}
            />
          )}

          {/* Import options (only shown after posts are processed) */}
          {posts && (
            <ImportOptionsComponent
              options={importOptions}
              setOptions={setImportOptions}
            />
          )}
        </>
      )}

      {/* Error alert */}
      <ErrorAlert error={error} />

      {/* Import progress (only shown during import) */}
      {importStatus === "IN_PROGRESS" && posts && (
        <ImportProgress
          numFinished={numFinished}
          skippedPosts={skippedPosts}
          erroredPosts={erroredPosts}
          postsRemaining={postsRemaining}
        />
      )}

      {/* Button row with Back and Action buttons */}
      <div className="flex items-center gap-3 mt-2">
        <BackButton onClick={onBack} />

        <ImportActionButton
          exportFile={exportFile}
          posts={posts}
          isLoading={isLoading}
          importFinished={importStatus === "FINISHED"}
          onAction={() => {
            if (posts) {
              handleImportPosts()
            } else {
              handleCreateNotes()
            }
          }}
        />
      </div>

      {/* Completion modal */}
      <ImportCompleteModal
        open={showCompleteModal}
        onOpenChange={setShowCompleteModal}
        job={importJobDetails}
        type="posts"
      />
    </div>
  )
}
